import mqtt, { type MqttClient, type IClientOptions } from 'mqtt';
import { useMqttStore } from '../stores/mqttStore';

type MessageHandler = (topic: string, payload: string) => void;

const INITIAL_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 30_000;
const RECONNECT_MULTIPLIER = 2;

let client: MqttClient | null = null;
let reconnectDelay = INITIAL_RECONNECT_MS;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const topicHandlers = new Map<string, Set<MessageHandler>>();

const getStore = () => useMqttStore.getState();

export const connect = (
  brokerUrl: string,
  stationId: string,
  options?: Partial<IClientOptions>
): void => {
  if (client?.connected) return;

  disconnect();

  const store = getStore();
  store.setBrokerUrl(brokerUrl);
  store.setStationId(stationId);

  const clientId = `voltbot-web-${stationId}-${Date.now()}`;

  client = mqtt.connect(brokerUrl, {
    clientId,
    clean: true,
    keepalive: 60,
    reconnectPeriod: 0,
    protocolVersion: 5,
    ...options
  });

  client.on('connect', () => {
    console.log('[MQTT] Connected to', brokerUrl);
    const s = getStore();
    s.setConnected(true);
    s.setLastError(null);
    s.setReconnectAttempts(0);
    reconnectDelay = INITIAL_RECONNECT_MS;
  });

  client.on('close', () => {
    console.log('[MQTT] Connection closed');
    getStore().setConnected(false);
  });

  client.on('error', (err: Error) => {
    console.error('[MQTT] Error:', err.message);
    getStore().setLastError(err.message);
  });

  client.on('offline', () => {
    getStore().setConnected(false);
    scheduleReconnect();
  });

  client.on('message', (topic: string, payload: Buffer) => {
    const message = payload.toString();
    if (topic.includes('/event') || topic.includes('/status')) {
      console.log('[MQTT RAW]', topic, message.substring(0, 120));
    }
    for (const [filter, handlers] of topicHandlers) {
      if (topicMatches(filter, topic)) {
        for (const handler of handlers) {
          try {
            handler(topic, message);
          } catch {
            // swallow per-handler errors
          }
        }
      }
    }
  });
};

export const disconnect = (): void => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  if (client) {
    client.removeAllListeners();
    if (client.connected) {
      client.end(true);
    }
    client = null;
  }

  topicHandlers.clear();
  const store = getStore();
  store.setConnected(false);
  store.reset();
};

export const subscribe = (
  topic: string,
  handler: MessageHandler,
  qos: 0 | 1 | 2 = 0
): void => {
  if (!topicHandlers.has(topic)) {
    topicHandlers.set(topic, new Set());
    client?.subscribe(topic, { qos });
    getStore().addSubscription(topic);
  }
  topicHandlers.get(topic)!.add(handler);
};

export const unsubscribe = (topic: string, handler?: MessageHandler): void => {
  const handlers = topicHandlers.get(topic);
  if (!handlers) return;

  if (handler) {
    handlers.delete(handler);
    if (handlers.size > 0) return;
  }

  topicHandlers.delete(topic);
  client?.unsubscribe(topic);
  getStore().removeSubscription(topic);
};

export const publish = (
  topic: string,
  payload: string,
  qos: 0 | 1 | 2 = 1
): void => {
  if (!client?.connected) return;
  client.publish(topic, payload, { qos });
};

export const publishCommand = (topic: string, payload: string): void => {
  publish(topic, payload, 2);
};

export const isConnected = (): boolean => {
  return client?.connected ?? false;
};

export const reconnect = (): void => {
  const store = getStore();
  const brokerUrl = store.brokerUrl || import.meta.env.VITE_MQTT_BROKER_URL;
  const stationId =
    store.stationId || import.meta.env.VITE_MQTT_STATION_ID || 'DEV-STATION-01';

  if (!brokerUrl) {
    console.warn('[MQTT] Cannot reconnect — no broker URL available');
    return;
  }

  console.log('[MQTT] Manual reconnect to', brokerUrl, 'station:', stationId);

  const options: Partial<IClientOptions> = {};
  const username = import.meta.env.VITE_MQTT_USERNAME;
  const password = import.meta.env.VITE_MQTT_PASSWORD;
  if (username) {
    options.username = username;
    options.password = password;
  }

  disconnect();
  connect(brokerUrl, stationId, options);
};

const scheduleReconnect = (): void => {
  if (reconnectTimer) return;

  const store = getStore();
  store.setReconnectAttempts(store.reconnectAttempts + 1);

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (client && !client.connected) {
      client.reconnect();
    }
    reconnectDelay = Math.min(
      reconnectDelay * RECONNECT_MULTIPLIER,
      MAX_RECONNECT_MS
    );
  }, reconnectDelay);
};

const topicMatches = (filter: string, topic: string): boolean => {
  if (filter === topic) return true;
  if (filter === '#') return true;

  const filterParts = filter.split('/');
  const topicParts = topic.split('/');

  for (let i = 0; i < filterParts.length; i++) {
    if (filterParts[i] === '#') return true;
    if (i >= topicParts.length) return false;
    if (filterParts[i] !== '+' && filterParts[i] !== topicParts[i])
      return false;
  }

  return filterParts.length === topicParts.length;
};
